import DSBox from "../../system-design/DSBox";
import DSGrid from "../../system-design/DSGrid";
import DSTypography from "../../system-design/DSTypography";
import DSCard from "../../system-design/DSCard";
import DSIconButton from "../../system-design/DSIconButton";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
}

export interface CalendarProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  renderDayContent?: (day: CalendarDay) => React.ReactNode;
  onDayClick?: (day: CalendarDay) => void;
  isDayClickable?: (day: CalendarDay) => boolean;
  minHeight?: number;
}

function Calendar({
  currentDate,
  onDateChange,
  renderDayContent,
  onDayClick,
  isDayClickable,
  minHeight = 120,
}: CalendarProps) {
  const getCalendarDays = (): CalendarDay[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const today = new Date();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startDay = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days: CalendarDay[] = [];

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthLastDay - i);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
      });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isToday =
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();

      days.push({
        date,
        isCurrentMonth: true,
        isToday,
      });
    }

    // Next month days (fill to complete 6 rows)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
      });
    }

    return days;
  };

  const handlePrevMonth = () => {
    onDateChange(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    onDateChange(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const calendarDays = getCalendarDays();

  return (
    <DSBox sx={{ p: 2 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <DSIconButton onClick={handlePrevMonth}>
          <FaChevronLeft />
        </DSIconButton>
        <DSTypography variant="h5">
          {currentDate.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </DSTypography>
        <DSIconButton onClick={handleNextMonth}>
          <FaChevronRight />
        </DSIconButton>
      </DSBox>

      <DSGrid container spacing={1}>
        {DAYS_OF_WEEK.map((day) => (
          <DSGrid item xs={12 / 7} key={day}>
            <DSBox sx={{ p: 1, textAlign: "center" }}>
              <DSTypography variant="subtitle2" color="textSecondary">
                {day}
              </DSTypography>
            </DSBox>
          </DSGrid>
        ))}

        {calendarDays.map((day, index) => {
          const isClickable = isDayClickable ? isDayClickable(day) : false;

          return (
            <DSGrid item xs={12 / 7} key={index}>
              <DSCard
                onClick={() => isClickable && onDayClick?.(day)}
                sx={{
                  minHeight,
                  p: 1,
                  border: day.isToday
                    ? "2px solid #8cbae8ff"
                    : "1px solid #e0e0e0",
                  backgroundColor: day.isCurrentMonth
                    ? "background.paper"
                    : "action.hover",
                  opacity: day.isCurrentMonth ? 1 : 0.6,
                  cursor: isClickable ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: isClickable ? "action.hover" : undefined,
                  },
                }}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <DSTypography
                    variant="body2"
                    sx={{
                      fontWeight: day.isToday ? "bold" : "normal",
                      color: day.isToday ? "primary.main" : "text.primary",
                      mb: 1,
                    }}
                  >
                    {day.date.getDate()}
                  </DSTypography>

                  {renderDayContent?.(day)}
                </DSBox>
              </DSCard>
            </DSGrid>
          );
        })}
      </DSGrid>
    </DSBox>
  );
}

export default Calendar;
